/**
 * ActionCard — PR4 right-pane Action mode UI.
 *
 * Shown when SourcePaneContext.pendingAction is set (chat surfaces an
 * ActionCard via the enriched tool_approval_required SSE event). Mounted
 * inside SourcePane in Action mode, takes the full pane.
 *
 * Layout (top to bottom):
 *   - Header strip: kind badge + title + status
 *   - Summary line
 *   - Validation panel (blocking issues disable Approve; warnings yellow)
 *   - Preview slot (reuses PR2 invoice/SO renderers via SourceRenderer)
 *   - Evidence chips (clicking opens that source as a new tab)
 *   - Editable params (only fields in operation.editable_fields)
 *   - Raw params JSON tree (collapsed)
 *   - Action bar: Approve / Edit / Reject + A/E/R shortcuts
 *
 * Security note: edits to non-editable fields are silently dropped by the
 * server. The Edit form here only renders fields listed in editable_fields
 * because the server filter is the source of truth.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Copy, Edit3,
  Loader2, RotateCcw, ShieldAlert, X,
} from 'lucide-react'
import { useSourcePane } from '../../contexts/SourcePaneContext'
import ApprovalCountdown from './ApprovalCountdown'
import { type ActionCard as ActionCardT, type Citation } from '../../types/cards'
import SourceRenderer from './SourceRenderer'
import { fmtMoney } from './renderers/_shared'
import { approveAction } from '../../lib/api'

export interface ActionCardViewProps {
  card: ActionCardT
  approvalId: string
  /** Server-sent deadline after which the gate stops accepting this approval.
   *  Shown so a card parked behind a collapsed pane cannot quietly go stale. */
  expiresAt?: string | null
  /** Called after a successful approve OR reject so the parent can clear pending state. */
  onResolved: (outcome: 'approved' | 'rejected') => void
}

// ── Helpers ───────────────────────────────────────────────────────────────

function isHighValue(card: ActionCardT, params: Record<string, unknown>): boolean {
  if (!card.high_value_field || card.high_value_threshold == null) return false
  const v = params[card.high_value_field]
  return typeof v === 'number' && v >= card.high_value_threshold
}

// ── Component ─────────────────────────────────────────────────────────────

export default function ActionCardView({ card, approvalId, expiresAt, onResolved }: ActionCardViewProps) {
  const { openSource } = useSourcePane()
  const blockingIssues = useMemo(
    () => (card.validation || []).filter(v => v.severity === 'blocking'),
    [card.validation],
  )
  const warnings = useMemo(
    () => (card.validation || []).filter(v => v.severity === 'warning'),
    [card.validation],
  )

  // Editable params start as the originals; reset returns to originals.
  const originalParams = useMemo<Record<string, unknown>>(
    () => ({ ...(card.operation?.params ?? {}) }),
    [card.operation?.params],
  )
  const [editParams, setEditParams] = useState<Record<string, unknown>>(originalParams)
  const [editing, setEditing] = useState(false)
  const [showJson, setShowJson] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<{ message: string; issues?: unknown[] } | null>(null)
  const [highValueConfirmOpen, setHighValueConfirmOpen] = useState(false)

  const editableFields = card.operation?.editable_fields ?? []
  const hasEdits = useMemo(
    () => editableFields.some(f => editParams[f] !== originalParams[f]),
    [editParams, originalParams, editableFields],
  )
  const approveDisabled = blockingIssues.length > 0 || submitting

  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const editBtnRef = useRef<HTMLButtonElement | null>(null)
  const rejectBtnRef = useRef<HTMLButtonElement | null>(null)

  // Keyboard shortcuts — A approve, E edit toggle, R reject. Ignored when
  // focus is in an editable element (matches PR1 SourcePane convention).
  useEffect(() => {
    function inEditable(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      return el.isContentEditable
    }
    function onKey(e: KeyboardEvent) {
      if (inEditable(e.target)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const k = e.key.toLowerCase()
      if (k === 'a' && !approveDisabled) { buttonRef.current?.click(); e.preventDefault() }
      else if (k === 'e' && editableFields.length > 0) { editBtnRef.current?.click(); e.preventDefault() }
      else if (k === 'r' && !submitting) { rejectBtnRef.current?.click(); e.preventDefault() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [approveDisabled, editableFields.length, submitting])

  async function doApprove(confirmedHighValue: boolean) {
    setServerError(null)
    setSubmitting(true)
    try {
      const editedDiff: Record<string, unknown> = {}
      for (const f of editableFields) {
        if (editParams[f] !== originalParams[f]) editedDiff[f] = editParams[f]
      }
      const resp = await approveAction(approvalId, {
        approved: true,
        edited_params: Object.keys(editedDiff).length > 0 ? editedDiff : undefined,
        confirmed_high_value: confirmedHighValue,
      })
      // 428 high-value precondition → server tells us to re-submit with confirm
      if (resp && (resp as { status_code?: number }).status_code === 428) {
        setHighValueConfirmOpen(true)
        return
      }
      onResolved('approved')
    } catch (e) {
      const err = e as { status?: number; data?: { detail?: { message?: string; issues?: unknown[] } | string } }
      if (err.status === 428) {
        setHighValueConfirmOpen(true)
      } else if (err.status === 422 && typeof err.data?.detail === 'object') {
        const d = err.data.detail as { message?: string; issues?: unknown[] }
        setServerError({ message: d.message || 'Validation rejected by server', issues: d.issues })
      } else {
        setServerError({ message: String((err as Error)?.message || err) })
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function doReject() {
    setSubmitting(true)
    try {
      await approveAction(approvalId, { approved: false })
      onResolved('rejected')
    } catch (e) {
      setServerError({ message: String((e as Error)?.message || e) })
    } finally {
      setSubmitting(false)
    }
  }

  const previewCitation: Citation = {
    id: `__action_preview_${card.id}`,
    label: card.title,
    kind: card.preview.kind,
    payload: card.preview.payload,
  }

  const highValue = isHighValue(card, editParams)
  const statusBadge = card.status === 'pending'
    ? 'bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300'
    : card.status === 'succeeded'
    ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
    : card.status === 'failed'
    ? 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300'
    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'

  return (
    <div className="h-full flex flex-col bg-white dark:bg-surface-dark">
      {/* Header */}
      <header className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-amber-50/40 dark:bg-amber-950/20">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert size={16} className="text-amber-600 dark:text-amber-400" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex-1 truncate">{card.title}</h2>
          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ${statusBadge}`}>
            {card.status}
          </span>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400">{card.summary}</p>
      </header>

      <div className="flex-1 overflow-auto">
        {/* Validation panel */}
        {(blockingIssues.length > 0 || warnings.length > 0) && (
          <section className="px-4 py-3 border-b border-gray-100 dark:border-gray-900 space-y-2">
            {blockingIssues.map((v, i) => (
              <div key={`b${i}`} className="flex items-start gap-2 p-2 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-xs text-red-800 dark:text-red-200">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div>
                  {v.field && <span className="font-mono font-semibold mr-1.5">{v.field}</span>}
                  {v.message}
                </div>
              </div>
            ))}
            {warnings.map((v, i) => (
              <div key={`w${i}`} className="flex items-start gap-2 p-2 rounded bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900 text-xs text-yellow-800 dark:text-yellow-200">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div>
                  {v.field && <span className="font-mono font-semibold mr-1.5">{v.field}</span>}
                  {v.message}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Preview (reuses SourceRenderer dispatch) */}
        <section className="border-b border-gray-100 dark:border-gray-900" style={{ minHeight: '40vh' }}>
          <SourceRenderer citation={previewCitation} />
        </section>

        {/* Editable params */}
        {editing && editableFields.length > 0 && (
          <section className="px-4 py-3 border-b border-gray-100 dark:border-gray-900">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">Edit params</h3>
              <button
                type="button"
                onClick={() => setEditParams(originalParams)}
                className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 hover:text-amtrend-blue"
              >
                <RotateCcw size={11} /> Reset
              </button>
            </div>
            <div className="space-y-2">
              {editableFields.map(f => (
                <ParamRow
                  key={f}
                  name={f}
                  value={editParams[f]}
                  onChange={(v) => setEditParams(p => ({ ...p, [f]: v }))}
                />
              ))}
            </div>
            {hasEdits && (
              <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400 italic">
                Server will re-validate edits. Edits to non-editable fields are silently dropped.
              </p>
            )}
          </section>
        )}

        {/* Evidence chips */}
        {card.evidence && card.evidence.length > 0 && (
          <section className="px-4 py-3 border-b border-gray-100 dark:border-gray-900">
            <h3 className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-2">Evidence</h3>
            <div className="flex flex-wrap gap-1.5">
              {card.evidence.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openSource(c)}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-surface-dark-overlay hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                >
                  <span className="text-[9px] uppercase tracking-wide font-semibold opacity-70">{c.kind.replace(/_/g, ' ')}</span>
                  <span className="truncate max-w-[20ch]">{c.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Raw params JSON */}
        <section className="px-4 py-3 border-b border-gray-100 dark:border-gray-900">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setShowJson(s => !s)}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide font-semibold text-gray-500 hover:text-amtrend-blue"
            >
              {showJson ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              Operation params
              <span className="font-mono lowercase text-gray-400 ml-2">{card.operation.type}</span>
            </button>
            <div className="flex items-center gap-3">
              <ApprovalCountdown expiresAt={expiresAt} className="text-[11px]" />
              <CopyApprovalIdButton approvalId={approvalId} />
            </div>
          </div>
          {showJson && (
            <pre className="mt-2 text-[10px] bg-gray-50 dark:bg-surface-dark-overlay p-3 rounded border border-gray-200 dark:border-gray-700 overflow-auto max-h-64">
              {JSON.stringify(editParams, null, 2)}
            </pre>
          )}
        </section>

        {/* Server error */}
        {serverError && (
          <section className="px-4 py-3 border-b border-gray-100 dark:border-gray-900">
            <div className="p-2 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-xs text-red-800 dark:text-red-200">
              <div className="font-semibold flex items-center gap-1.5 mb-1">
                <AlertTriangle size={12} />
                {serverError.message}
              </div>
              {serverError.issues && (
                <ul className="mt-1 list-disc pl-5 space-y-0.5">
                  {(serverError.issues as { field?: string; message: string }[]).map((it, i) => (
                    <li key={i}>
                      {it.field && <span className="font-mono mr-1">{it.field}:</span>}
                      {it.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Action bar */}
      <footer className="sticky bottom-0 z-10 bg-white dark:bg-surface-dark border-t border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center gap-2">
        <button
          ref={rejectBtnRef}
          type="button"
          onClick={doReject}
          disabled={submitting}
          className="px-3 py-2 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay disabled:opacity-50"
        >
          <span className="inline-flex items-center gap-1.5"><X size={14} /> Reject <kbd className="text-[9px] px-1 rounded bg-gray-100 dark:bg-gray-800 font-normal">R</kbd></span>
        </button>
        <button
          ref={editBtnRef}
          type="button"
          onClick={() => setEditing(e => !e)}
          disabled={submitting || editableFields.length === 0}
          className="px-3 py-2 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay disabled:opacity-50"
        >
          <span className="inline-flex items-center gap-1.5"><Edit3 size={14} /> {editing ? 'Done editing' : 'Edit'} <kbd className="text-[9px] px-1 rounded bg-gray-100 dark:bg-gray-800 font-normal">E</kbd></span>
        </button>
        <div className="flex-1" />
        <button
          ref={buttonRef}
          type="button"
          onClick={() => highValue ? setHighValueConfirmOpen(true) : doApprove(false)}
          disabled={approveDisabled}
          aria-disabled={approveDisabled}
          className={
            'px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ' +
            (approveDisabled ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed' : 'bg-amtrend-blue hover:bg-blue-700')
          }
        >
          <span className="inline-flex items-center gap-1.5">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Approve
            <kbd className="text-[9px] px-1 rounded bg-white/20 font-normal">A</kbd>
          </span>
        </button>
      </footer>

      {/* High-value confirmation modal */}
      {highValueConfirmOpen && (
        <HighValueModal
          card={card}
          params={editParams}
          onCancel={() => setHighValueConfirmOpen(false)}
          onConfirm={() => { setHighValueConfirmOpen(false); doApprove(true) }}
        />
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────

function ParamRow({ name, value, onChange }: { name: string; value: unknown; onChange: (v: unknown) => void }) {
  const type = typeof value === 'number' ? 'number' : 'text'
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="font-mono text-gray-500 dark:text-gray-400 w-32 truncate">{name}</span>
      <input
        type={type}
        value={value == null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value
          onChange(type === 'number' ? (raw === '' ? null : Number(raw)) : raw)
        }}
        className="flex-1 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-amtrend-blue"
      />
    </label>
  )
}

function CopyApprovalIdButton({ approvalId }: { approvalId: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(approvalId).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      title="Copy approval_id (for tamper testing / debugging)"
      className="inline-flex items-center gap-1 text-[10px] font-mono text-gray-400 hover:text-amtrend-blue"
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
      {copied ? 'copied' : approvalId}
    </button>
  )
}

function HighValueModal({
  card, params, onCancel, onConfirm,
}: {
  card: ActionCardT
  params: Record<string, unknown>
  onCancel: () => void
  onConfirm: () => void
}) {
  const field = card.high_value_field || 'amount'
  const value = params[field]
  const threshold = card.high_value_threshold ?? 0
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white dark:bg-surface-dark rounded-lg shadow-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={18} className="text-red-600 dark:text-red-400" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Confirm high-value action</h3>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          The <span className="font-mono">{field}</span> ({typeof value === 'number' ? fmtMoney(value) : String(value)})
          is at or above the threshold of {fmtMoney(threshold)}. Confirm you want to proceed.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay">Cancel</button>
          <button type="button" onClick={onConfirm} className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700">I confirm — proceed</button>
        </div>
      </div>
    </div>
  )
}
